import { Inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { EMPTY, Observable, of } from 'rxjs';
import { ApiClientConfig } from './api-client-config';
import {
  OrganizationIdByDomainResponse,
  OrgDisableRequest,
  OrgEnableRequest,
} from '@primatoo-ms-admin';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';
import { OrganizationThemeResponse } from '@primatoo-ms-organization';
import { AgGridLangMap, DownloadFormat, DownloadResponse, GetLoginModulesResponse } from '@pt/pt-models';
import { AdmOrganization } from 'it/primatoo/openapi/api/adm/schemas/organization/AdmOrganization';
import { ColumnType } from 'it/primatoo/openapi/api/org/schemas/common/ColumnType';
import { ThreatValue } from 'it/primatoo/openapi/api/org/schemas/common/ThreatValue';
import { UpdateOrganizationRequest } from 'it/primatoo/openapi/api/adm/schemas/organization/UpdateOrganizationRequest';
import { CreateOrganizationRequest } from 'it/primatoo/openapi/api/adm/schemas/organization/CreateOrganizationRequest';
import { CreateOrganizationResponse } from 'it/primatoo/openapi/api/adm/schemas/organization/CreateOrganizationResponse';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';
import { GetTicketInfoResponse } from 'it/primatoo/openapi/api/org/schemas/organization/GetTicketInfoResponse';
import { ApiClientUtils } from '../api-client.utils';
import * as FileSaver from 'file-saver';

/**
 * Non inserito providedIn: 'root' ma utilizzato in app.module per il pre-caricamento del tema
 */
@Injectable()
export class OrganizationsService {
  private readonly endpoint;
  private readonly endpointOrgV2;
  private readonly endpointAdmV2;

  constructor(
    @Inject(ApiClientConfig) private config: ApiClientConfig,
    private httpClient: HttpClient
  ) {
    this.endpoint = this.config.endPoint;
    this.endpointOrgV2 = this.config.endPoint + '/api/org/v2/organizations/';
    this.endpointAdmV2 = this.config.endPoint + '/api/adm/v2/organizations/';
  }

  // API DOC V2
  public getSupportInfo(): Observable<GetTicketInfoResponse> {
    const url = this.endpointOrgV2 + `support-info/`;
    return this.httpClient.get<GetTicketInfoResponse>(url);
  }

  public getOrganizationSelf(): Observable<Organization> {
    const url = this.endpointOrgV2 + `self`;
    return this.httpClient.get<Organization>(url);
  }

  // API V1
  public getThreats(): Observable<ThreatValue[]> {
    const url = this.endpoint + `/api/org/threats/`;
    return this.httpClient.get<ThreatValue[]>(url);
  }

  public initializationTemplate(): Observable<HttpResponse<Blob>> {
    const url = this.endpoint + `/api/org/organizations/template`;
    return this.httpClient.get(url, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  public organizationInitialization(
    file: File
  ): Observable<HttpEvent<void> | void> {
    const url = this.endpoint + `/api/org/organizations/init`;
    const formData = new FormData();
    if (file) {
      formData.append('file', file, file.name);
      const uploadRequest = new HttpRequest('POST', url, formData, {
        reportProgress: true,
      });
      return this.httpClient.request<void>(uploadRequest);
    }
    return EMPTY;
  }

  public organizationExport(): Observable<HttpResponse<Blob>> {
    const url = this.endpoint + `/api/org/organizations/export`;
    return this.httpClient.get(url, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  public organizationConfigurationInitialization(file: File): Observable<any> {
    const url = this.endpoint + `/api/org/organizations/template-config/init`;
    const formData = new FormData();
    if (file) {
      formData.append('file', file, file.name);
      const uploadRequest = new HttpRequest('POST', url, formData, {
        reportProgress: true,
        responseType: 'text',
      });
      return this.httpClient.request<void>(uploadRequest);
    }
    const uploadRequest = new HttpRequest('POST', url, formData, {
      reportProgress: true,
      responseType: 'text',
    });
    return this.httpClient.request<void>(uploadRequest);
  }

  public organizationConfigurationExport(): Observable<any> {
    const url = this.endpoint + `/api/org/organizations/template-config/export`;
    return this.httpClient.get(url, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  public getAllOrganizations(): Observable<AdmOrganization[]> {
    const url = this.endpoint + `/api/adm/organizations/`;
    return this.httpClient.get<AdmOrganization[]>(url);
  }

  public getColEnabled(
    agGridLangMap: AgGridLangMap
  ): Observable<ColumnWithLocalization[]> {
    return of([
      {
        type: ColumnType.TEXT,
        column: 'name',
        label: agGridLangMap.getOf('name'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.TEXT,
        column: 'domain',
        label: agGridLangMap.getOf('domain'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.TEXT,
        column: 'uuid',
        label: agGridLangMap.getOf('uuid'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.BOOLEAN,
        column: 'dedicatedSchema',
        label: agGridLangMap.getOf('dedicatedSchema'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.TEXT,
        column: 'dataSource',
        label: agGridLangMap.getOf('dataSource'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.TEXT,
        column: 'status',
        label: agGridLangMap.getOf('status'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.BOOLEAN,
        column: 'enabled',
        label: agGridLangMap.getOf('enabled'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.DATE,
        column: 'createAt',
        size: 300,
        label: agGridLangMap.getOf('createAt'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
    ]);
  }

  public getOrganizationByUuid(uuid: string): Observable<AdmOrganization> {
    const url = this.endpoint + '/api/adm/organizations/' + uuid + '/';
    return this.httpClient.get<AdmOrganization>(url);
  }

  public updateOrganization(
    uuid: string,
    updateRequest: UpdateOrganizationRequest
  ): Observable<void> {
    const url = this.endpointAdmV2 + uuid + '/';
    return this.httpClient.post<void>(url, updateRequest);
  }
  public createOrganization(
    organization: CreateOrganizationRequest
  ): Observable<CreateOrganizationResponse> {
    return this.httpClient.post<CreateOrganizationResponse>(
      this.endpointAdmV2,
      organization
    );
  }

  public deleteOrganization(uuid: string): Observable<any> {
    const url = this.endpoint + '/api/org/organizations/' + uuid + '/';
    return this.httpClient.delete<any>(url);
  }

  public deleteOrganizationFromConsole(uuid: string): Observable<any> {
    const url = this.endpoint + '/api/adm/organizations/' + uuid + '/';
    return this.httpClient.delete<never>(url);
  }

  public disableOrganization(
    orgDisableRequest: OrgDisableRequest
  ): Observable<any> {
    const url = this.endpoint + '/api/adm/organizations/disable';
    return this.httpClient.post<any>(url, orgDisableRequest);
  }

  public enableOrganization(
    orgEnableRequest: OrgEnableRequest
  ): Observable<any> {
    const url = this.endpoint + '/api/adm/organizations/enable';
    return this.httpClient.post<any>(url, orgEnableRequest);
  }

  public getOrganizationsColEnabled(): Observable<ColumnWithLocalization[]> {
    const url = this.endpoint + `/api/adm/organizations/columns`;
    return this.httpClient.get<ColumnWithLocalization[]>(url);
  }

  public getOrganizationDomain(
    domain: string
  ): Observable<OrganizationIdByDomainResponse> {
    const url =
      this.endpoint + `/api/adm/organizations/by-domain/?orgDomain=${domain}`;
    return this.httpClient.get<OrganizationIdByDomainResponse>(url);
  }

  public getOrganizationTheme(
    domain: string,
    orgId: string
  ): Observable<OrganizationThemeResponse> {
    localStorage.setItem('X-TenantID', domain);
    const url = this.endpoint + `/api/org/organizations/${orgId}/theme/`;
    return this.httpClient.get<OrganizationThemeResponse>(url);
  }

  public getAppVersion(): Observable<{ appVersion: string }> {
    const url = this.endpoint + '/api/org/system/app-version/';
    return this.httpClient.get<{ appVersion: string }>(url);
  }

  public getTimezoneList(): Observable<{ timezones: string[] }> {
    const url = this.endpoint + '/api/org/system/timezones/';
    return this.httpClient.get<{ timezones: string[] }>(url);
  }

  // ------ Logo ------ //

  public updateLogo(file: File): Observable<void> {
    const url = this.endpointOrgV2 + `self/logo`;
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<void>(url, formData);
  }

  public deleteLogo(): Observable<void> {
    const url = this.endpointOrgV2 + `self/logo`;
    return this.httpClient.delete<void>(url);
  }

  public updateResellerLogo(file: File): Observable<void> {
    const url = this.endpoint + `api/org/organizations/self/reseller-logo`;
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<void>(url, formData);
  }

  public deleteResellerLogo(): Observable<void> {
    const url = this.endpoint + `api/org/organizations/self/reseller-logo`;
    return this.httpClient.delete<void>(url);
  }

  public updatePdfLogo(file: File): Observable<void> {
    const url = this.endpointOrgV2 + `self/pdf-logo`;
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<void>(url, formData);
  }

  public deletePdfLogo(): Observable<void> {
    const url = this.endpointOrgV2 + `self/pdf-logo`;
    return this.httpClient.delete<void>(url);
  }

  public activeLoginModule(): Observable<GetLoginModulesResponse> {
    const url = this.endpointOrgV2 + 'login-modules/';
    return this.httpClient.get<GetLoginModulesResponse>(url);
  }

  public downloadManual() {
    const url = this.endpointOrgV2 + 'user-manual';
    ApiClientUtils.downloadComplete(this.httpClient, url, DownloadFormat.PDF);
  }
}
